import { IAppNavigationItemIcon, IColumnsProps, IOperateData, OPERATE_ACT_TYPE } from '../../interface/interface';

import { getAppNavigationLangInfo, getMaxLengthBySystem, onCheckLabelBySystem } from '../../utils';
import { lang } from '@umu/ukit-lang';
import { Button, Iconfont, SnackBar, Switch } from '@umu/umu-design';
import { classSet } from '@umu/umu-dom';
import React from 'react';
import { IconListShow } from '../iconListShow';
import { CONFIG_MAX_ENABLE_COUNT, getUseAppNavIconList, getUseNavDefaultInfo } from '../../dataFlow';
import { If } from 'common/src/components';
import { LabelInput } from '../labelInput';
import cloneDeep from 'lodash/cloneDeep';
import { INavigationItemInfo, NAV_ENABLE_STATUS, NavigationPageType } from '@umu/umu-api';
import merge from 'lodash/merge';
/**
 * @desc 覆盖更新指定行数据
 */
const getCoverData = (list: INavigationItemInfo[], data: IOperateData) => {
    const { index, key = 'icon', rowData } = data;
    const result = cloneDeep(list);
    if (result[index]) {
        (result[index] as any)[key] = rowData;
    }
    return result;
};
/**
 * @desc 合并更新指定行数据
 */
const getMergerList = (list: INavigationItemInfo[], data: IOperateData) => {
    const { index, rowData } = data;
    const result = cloneDeep(list);
    result[index] = merge({}, result[index], rowData);
    return result;
};
/**
 * @desc 检查启用的导航数量
 */
const onCheckEnableCount = (
    list: INavigationItemInfo[],
    data: IOperateData,
    enableMaxCount = CONFIG_MAX_ENABLE_COUNT,
) => {
    const newList = getMergerList(list, data);
    let isValid = true;
    const countEnable = newList.filter((item) => Number(item.is_enabled) === NAV_ENABLE_STATUS.ENABLE).length;
    if (countEnable > enableMaxCount) {
        SnackBar.show({
            message: lang('启用的导航最多可设置${0}个').replace('${0}', enableMaxCount),
        });
        isValid = false;
    }
    return { isValid, result: newList };
};
const getUseNavigationColumns = (info: IColumnsProps) => {
    const { isValid, isShowIconList = false, isShowColor = false, onOperateCallback } = info;
    const [firstLang, secondLang, showSecondLang] = getAppNavigationLangInfo();
    const columns = [
        {
            title: firstLang.title,
            dataIndex: firstLang.key,
            key: firstLang.key,
            width: 150,
            className: 'first-lang',
            render(_value: string, rowData: INavigationItemInfo, index: number) {
                const { title } = rowData;
                return (
                    <LabelInput
                        isValid={isValid}
                        maxLength={getMaxLengthBySystem(firstLang.key)}
                        onValidValue={(val) => onCheckLabelBySystem(firstLang.key, val)}
                        value={title[firstLang.key] || ''}
                        onChange={(val) =>
                            onOperateCallback(OPERATE_ACT_TYPE.UPDATE_TITLE, {
                                index,
                                rowData: {
                                    title: {
                                        [firstLang.key]: val,
                                    },
                                } as INavigationItemInfo,
                            })
                        }
                    />
                );
            },
        },
        {
            title: secondLang.title || '',
            dataIndex: secondLang.key,
            key: secondLang.key,
            hide: !showSecondLang,
            width: 150,
            className: 'second-lang',
            render(_value: string, rowData: INavigationItemInfo, index: number) {
                const { title } = rowData;
                return (
                    <LabelInput
                        isValid={isValid}
                        value={title[secondLang.key] || ''}
                        maxLength={getMaxLengthBySystem(secondLang.key)}
                        onValidValue={(val) => onCheckLabelBySystem(secondLang.key, val)}
                        onChange={(val) =>
                            onOperateCallback(OPERATE_ACT_TYPE.UPDATE_TITLE, {
                                index,
                                rowData: {
                                    title: {
                                        [secondLang.key]: val,
                                    },
                                } as INavigationItemInfo,
                            })
                        }
                    />
                );
            },
        },
        {
            title: lang('展示'),
            dataIndex: 'is_enabled',
            key: 'is_enabled',
            width: 150,
            className: 'enabled-status',
            render(value: string, rowData: INavigationItemInfo, index: number) {
                const { can_close } = rowData;
                const defaultChecked = Number(value) === NAV_ENABLE_STATUS.ENABLE;
                const isDisableClose = Number(can_close) === NAV_ENABLE_STATUS.DISABLE;
                return (
                    <Switch
                        defaultChecked={defaultChecked}
                        disabled={isDisableClose}
                        onChange={(val) =>
                            onOperateCallback(OPERATE_ACT_TYPE.UPDATE_STATUS, {
                                index,
                                rowData: {
                                    ...rowData,
                                    is_enabled: val ? NAV_ENABLE_STATUS.ENABLE : NAV_ENABLE_STATUS.DISABLE,
                                },
                            })
                        }
                    />
                );
            },
        },
        {
            title: `PC${lang('图标')}`,
            dataIndex: 'pc_icon',
            key: 'pc_icon',
            width: 150,
            className: 'icon-td',
            render(value: IAppNavigationItemIcon, rowData: INavigationItemInfo, index: number) {
                const { title, name } = rowData;
                const defaultInfo = getUseNavDefaultInfo(name as string);
                return (
                    <IconListShow
                        value={value}
                        isHideNormalIcon
                        isShowIconList={isShowIconList}
                        isShowColor={isShowColor}
                        iconList={getUseAppNavIconList(true)}
                        resetInfo={defaultInfo.defaultIconInfo}
                        navName={title[firstLang.key] || ''}
                        onChange={(val) => {
                            onOperateCallback(OPERATE_ACT_TYPE.ICON_CHANGE, {
                                index,
                                key: 'pc_icon',
                                rowData: val as INavigationItemInfo,
                            });
                        }}
                    />
                );
            },
        },
        {
            title: `App${lang('图标')}`,
            dataIndex: 'icon',
            key: 'icon',
            width: 150,
            className: 'icon-td',
            render(value: IAppNavigationItemIcon, rowData: INavigationItemInfo, index: number) {
                const { title, name } = rowData;
                const defaultInfo = getUseNavDefaultInfo(`${name as string}_app`);
                return (
                    <IconListShow
                        value={value}
                        isHideNormalIcon
                        isForceSvgToImg
                        isShowIconList={isShowIconList}
                        isShowColor={isShowColor}
                        iconList={getUseAppNavIconList(true)}
                        resetInfo={defaultInfo.defaultIconInfo}
                        navName={title[firstLang.key] || ''}
                        onChange={(val) =>
                            onOperateCallback(OPERATE_ACT_TYPE.ICON_CHANGE, {
                                index,
                                key: 'icon',
                                rowData: val as INavigationItemInfo,
                            })
                        }
                    />
                );
            },
        },
        {
            title: lang('操作'),
            dataIndex: 'operate',
            key: 'operate',
            width: 150,
            className: 'navigation-operate-td',
            render(_value: string, rowData: INavigationItemInfo, index: number) {
                const { is_enabled } = rowData;
                const isStatusDisabled = Number(is_enabled) === NAV_ENABLE_STATUS.DISABLE;
                const params = {
                    index,
                    rowData,
                };
                return (
                    <div className="navigation-item-operate-info">
                        <div>
                            <If condition={!isStatusDisabled}>
                                <Button
                                    type="text"
                                    className="btn-split-col"
                                    disabled={!index}
                                    isOnlyChildren
                                    onClick={() => onOperateCallback(OPERATE_ACT_TYPE.UP_MOVE, params)}
                                >
                                    {lang('上移')}
                                </Button>
                            </If>
                        </div>
                        <div
                            className={classSet({
                                'navigation-item-operate-info-move': true,
                                'navigation-item-operate-info-move-disabled': isStatusDisabled,
                            })}
                        >
                            <Iconfont type="UprightMore" className="icon-move-upright-more" />
                            <Iconfont type="UprightMore" className="icon-move-upright-more" />
                        </div>
                    </div>
                );
            },
        },
    ];
    return {
        columns: columns.filter((item) => !item.hide),
        showSecondLang,
    };
};
/**
 * @desc 获取表格列数据
 */
const getAppNavigationColumns = (info: IColumnsProps) => {
    const { pageType } = info;
    if (pageType === NavigationPageType.USE_NAVIGATION) {
        // 暂时只有USE导航，其他导航暂时不迁移
        return getUseNavigationColumns(info);
    }
    return {
        columns: [],
        showSecondLang: false,
    };
};

export { getCoverData, getMergerList, onCheckEnableCount, getAppNavigationColumns };
